import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { IPaginatedResponse } from 'app/projects/subentity/src/lib/interfaces/paginated-response.interface';
import { peekAsyncJobIdFromResponse } from 'app/services/http-async-job-response.util';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { OfferingType } from '../enums/offering-type.enum';
import { IChangeCourseOfferingCategoryActionPayload } from '../interfaces/change-course-offering-category.action.payload.interface';
import { ICourseOfferingByCategoryQuery } from '../interfaces/course-offering-by-category.query.interface';
import { ICourseOfferingWithBreadcrumbQuery } from '../interfaces/course-offering-with-breadcrumb.query.interface';
import { ICourseOfferingQuery } from '../interfaces/course-offering.query.interface';
import { ICourseOfferingRemovalImpactQuery } from '../interfaces/course-offering-removal-impact.query.interface';
import { IDeleteCourseOfferingResult } from '../interfaces/delete-course-offering-result.interface';
import { IUpdateCourseOfferingPricePayload } from '../interfaces/update-course-offering-price.payload.interface';
import { ICourseOfferingExtendedAttribute } from '../interfaces/couse-offering-extended-attribute.interface';
import { ICreateOfferingActionPayload } from '../interfaces/create-offering.action.payload.interface';
import { ICreateProgramOfferingActionPayload } from '../interfaces/create-program-offering.action.payload.interface';
import { ChangeCourseOfferingCategoryCommand } from '../models/change-course-offering-category.command.model';
import { CourseOfferingAudienceCommand } from '../models/course-offering-audience.command.model';
import { CourseOfferingsByCategory } from '../models/course-offering-by-group.model';
import { CourseOfferingExtendedAttributeCommand } from '../models/course-offering-extended-attribute.command.model';
import { CourseOfferingCommand } from '../models/course-offering.command.model';
import { CourseOffering } from '../models/course-offering.model';
import { ProgramOfferingCommand } from '../models/program-offering.command.model';
import { UpdateCourseOfferingCommand } from '../models/update-course-offering.command.model';

@Injectable()
export class CourseOfferingApiService extends EntityService<ICourseOfferingByCategoryQuery, CourseOfferingCommand> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'course-structure/course-offerings');
    }

    getAll(categoryId: string): Observable<CourseOfferingsByCategory[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new CourseOfferingsByCategory(query))));
    }

    create(entity: ICreateOfferingActionPayload): Observable<CourseOffering> {
        const data = new CourseOfferingCommand(entity);
        return this.http.post<ICourseOfferingQuery>(`${this.environment.apiUrl}/${this.entities}`, data).pipe(
            map((query) => new CourseOffering(query)),
            switchMap((courseOffering) => {
                if (entity.minimumAttendance == null || !courseOffering.courseTemplate?.id) {
                    return of(courseOffering);
                }

                return this.http
                    .patch(`${this.environment.apiUrl}/modules/${courseOffering.courseTemplate.id}/minimum-attendance`, {
                        minimum_attendance: entity.minimumAttendance,
                    })
                    .pipe(map(() => courseOffering));
            })
        );
    }

    read(id: string): Observable<CourseOffering> {
        return this.http.get<ICourseOfferingQuery>(`${this.environment.apiUrl}/${this.entities}/${id}`).pipe(map((query) => new CourseOffering(query, query.offering_group)));
    }

    delete(id: string): Observable<IDeleteCourseOfferingResult> {
        return this.http.delete<unknown>(`${this.apiUrl}/${this.entities}/${id}`, { observe: 'response' }).pipe(
            map((resp: HttpResponse<unknown>) => {
                const jobId = peekAsyncJobIdFromResponse(resp) ?? this._peekAsyncJobIdFromBody(resp.body);

                // 202 + job id: async deletion; 204 / other success without job: sync deletion (e.g. no executions)
                return { id: jobId };
            })
        );
    }

    private _peekAsyncJobIdFromBody(body: unknown): string | null {
        if (!body || typeof body !== 'object') {
            return null;
        }

        const record = body as Record<string, unknown>;
        const raw = record['async_job_id'] ?? record['id'];
        return raw != null && String(raw) !== '' ? String(raw) : null;
    }

    getRemovalImpact(id: string): Observable<ICourseOfferingRemovalImpactQuery> {
        return this.http.get<ICourseOfferingRemovalImpactQuery>(`${this.environment.apiUrl}/${this.entities}/${id}/removal-impact`);
    }

    update(entity: CourseOffering): Observable<CourseOffering> {
        const data = new UpdateCourseOfferingCommand(entity);

        return this.http.patch<ICourseOfferingQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new CourseOffering(query)));
    }

    updatePrice(courseOfferingId: string, payload: IUpdateCourseOfferingPricePayload): Observable<CourseOffering> {
        return this.http
            .patch<ICourseOfferingQuery>(`${this.environment.apiUrl}/course-structure/course-offerings/${courseOfferingId}`, payload)
            .pipe(map((query) => new CourseOffering(query)));
    }

    updateOfferingOrder(entity: CourseOffering): Observable<CourseOffering> {
        const data = new UpdateCourseOfferingCommand(entity);

        return this.http.patch<ICourseOfferingQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, data).pipe(map((query) => new CourseOffering(query)));
    }

    updateProgramOfferingCompletion(entity: CourseOffering): Observable<CourseOffering> {
        return this.http
            .patch<ICourseOfferingQuery>(`${this.apiUrl}/${this.entities}/${entity.id}`, { completion_rule: entity.completionRule })
            .pipe(map((query) => new CourseOffering(query)));
    }

    updateOfferingCategory(entity: IChangeCourseOfferingCategoryActionPayload): Observable<CourseOffering> {
        const data = new ChangeCourseOfferingCategoryCommand(entity);

        return this.http.patch<ICourseOfferingQuery>(`${this.apiUrl}/${this.entities}/${entity.courseOfferingId}/category`, data).pipe(map((query) => new CourseOffering(query)));
    }

    copyToOfferingCategory(courseOfferingId: string, targetCategoryId: string): Observable<{ id: string; async_job_id: string }> {
        return this.http.post<{ id: string; async_job_id: string }>(`${this.apiUrl}/course-offerings/${courseOfferingId}/copy-to`, {
            target_category_id: targetCategoryId,
        });
    }

    getCourseOfferingsByCategory(
        categoryId: string,
        search: string,
        courseOfferingType: OfferingType,
        tags?: string[],
        types?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[]
    ): Observable<ICourseOfferingByCategoryQuery> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (courseOfferingType) {
            params = params.set('filter[coType]', courseOfferingType);
        }

        if (tags.length) {
            params = params.set('filter[tag]', tags.toString());
        }

        if (extendedAttributesFilters?.length) {
            let attributeValues = {};

            extendedAttributesFilters.forEach((attr) => {
                if (attr.value) {
                    if (!attributeValues[attr.attributeName]) {
                        attributeValues[attr.attributeName] = [];
                    }

                    attributeValues[attr.attributeName].push(attr.value);
                } else {
                    params = params.set(`filter[${attr.attributeName}][gte]`, attr.rangeValue1);
                    params = params.set(`filter[${attr.attributeName}][lte]`, attr.rangeValue2);
                }
            });

            Object.keys(attributeValues).forEach((key) => {
                params = params.append(`filter[${key}]`, attributeValues[key].join(','));
            });
        }

        if (types?.length) {
            params = params.append(`filter[type]`, types.map((type) => type).toString());
        }

        return this.http.get<ICourseOfferingByCategoryQuery>(`${this.environment.apiUrl}/course-structure/categories/${categoryId}/course-offerings`, { params });
    }

    searchOfferingsByCategory(
        categoryId: string,
        search = '',
        tags?: string[],
        types?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        columns?: string[]
    ): Observable<IPaginatedResponse<ICourseOfferingWithBreadcrumbQuery>> {
        let params = new HttpParams();

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (tags.length) {
            params = params.set('filter[tag]', tags.toString());
        }

        if (extendedAttributesFilters?.length) {
            let attributeValues = {};

            extendedAttributesFilters.forEach((attr) => {
                if (attr.value) {
                    if (!attributeValues[attr.attributeName]) {
                        attributeValues[attr.attributeName] = [];
                    }

                    attributeValues[attr.attributeName].push(attr.value);
                } else {
                    params = params.set(`filter[${attr.attributeName}][gte]`, attr.rangeValue1);
                    params = params.set(`filter[${attr.attributeName}][lte]`, attr.rangeValue2);
                }
            });

            Object.keys(attributeValues).forEach((key) => {
                params = params.append(`filter[${key}]`, attributeValues[key].join(','));
            });
        }

        if (columns?.length) {
            params = params.set('filter[columns]', columns.map((column) => column).toString());
        }

        if (types?.length) {
            params = params.append(`filter[type]`, types.map((type) => type).toString());
        }

        return this.http.get<IPaginatedResponse<ICourseOfferingWithBreadcrumbQuery>>(
            `${this.environment.apiUrl}/course-structure/categories/${categoryId}/course-offerings/search`,
            { params }
        );
    }

    fetchAssignedCertificates(courseOfferingId: string): Observable<{ name: string }[]> {
        return this.http.get<{ name: string }[]>(`${this.apiUrl}/certificate-of-attendance/course-offerings/${courseOfferingId}/assigned-certificates`).pipe(
            map((queries) =>
                queries.map((query) => {
                    return { name: query.name };
                })
            )
        );
    }

    createProgramOffering(entity: ICreateProgramOfferingActionPayload): Observable<CourseOffering> {
        const data = new ProgramOfferingCommand(entity);
        return this.http.post<ICourseOfferingQuery>(`${this.environment.apiUrl}/course-structure/program-offerings`, data).pipe(
            map((query) => new CourseOffering(query)),
            switchMap((courseOffering) => {
                if (entity.minimumAttendance == null || !courseOffering.courseTemplate?.id) {
                    return of(courseOffering);
                }

                return this.http
                    .patch(`${this.environment.apiUrl}/modules/${courseOffering.courseTemplate.id}/minimum-attendance`, {
                        minimum_attendance: entity.minimumAttendance,
                    })
                    .pipe(map(() => courseOffering));
            })
        );
    }

    // linkedAudiences contains all linked audiences, e.g. if we want to unlink audience we don't send id from that audience
    linkAndUnlinkAudiences(courseOfferingId: string, linkedAudiences: { id: string }[], removeUsers?: boolean): Observable<CourseOffering> {
        let params = new HttpParams();
        const data = new CourseOfferingAudienceCommand(linkedAudiences);

        if (removeUsers) {
            params = params.set('removeUsers', removeUsers);
        }

        return this.http
            .post<ICourseOfferingQuery>(`${this.environment.apiUrl}/${this.entities}/${courseOfferingId}/linked-audiences`, data, { params })
            .pipe(map((query) => new CourseOffering(query)));
    }

    async updateExtendedAttributeCourseOfferingValue(entity: ICourseOfferingExtendedAttribute): Promise<CourseOffering> {
        const data = new CourseOfferingExtendedAttributeCommand(entity);

        return this.http
            .put<ICourseOfferingQuery>(`${this.apiUrl}/extended-attribute/course-offering/value`, data)
            .pipe(map((query) => new CourseOffering(query)))
            .toPromise();
    }
}
